import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  FormControlLabel, Checkbox, TextField, Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useAtsSenderName from '../../hooks/useAtsSenderName';

const resolveTemplate = (text, { candidate, cargo, empresa }) =>
  String(text || '')
    .replace(/\{\{\s*candidato\s*\}\}/g, candidate || '')
    .replace(/\{\{\s*cargo\s*\}\}/g, cargo || '')
    .replace(/\{\{\s*empresa\s*\}\}/g, empresa || '');

/**
 * Diálogo de descarte de un candidato (con opción de correo de no selección).
 * Reutilizado en el tab Candidatos y en el drawer del pipeline.
 *
 * onConfirm recibe { sendEmail, emailSubject, emailBody, senderName }. El llamador
 * hace la llamada al backend y actualiza el estado.
 */
const AtsDiscardDialog = ({
  open,
  onClose,
  application,
  rejectionTemplate = { subject: '', body: '' },
  autoSendDefault = false,
  positionName = '',
  orgName = '',
  onConfirm,
}) => {
  const { t } = useTranslation();

  const [sendEmail, setSendEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const { senderName, setSenderName } = useAtsSenderName(open);

  useEffect(() => {
    if (open && application) {
      const candidate = application.candidateName || application.candidateEmail || '';
      setEmailSubject(resolveTemplate(rejectionTemplate?.subject, { candidate, cargo: positionName, empresa: orgName }));
      setEmailBody(resolveTemplate(rejectionTemplate?.body, { candidate, cargo: positionName, empresa: orgName }));
      setSendEmail(!!autoSendDefault);
    }
  }, [open, application, rejectionTemplate, autoSendDefault, positionName, orgName]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ats.detail.discardTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {t('ats.detail.discardConfirm', {
            name: application?.candidateName || application?.candidateEmail,
          })}
        </Typography>

        <FormControlLabel
          sx={{ mt: 2 }}
          control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />}
          label={t('ats.detail.discardSendEmail')}
        />

        {sendEmail && (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label={t('ats.config.emailSender.fieldLabel')}
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              inputProps={{ maxLength: 80 }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('ats.detail.discardEmailSubject')}
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={5}
              label={t('ats.detail.discardEmailBody')}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{t('cancel')}</Button>
        <Button
          onClick={() => onConfirm && onConfirm({ sendEmail, emailSubject, emailBody, senderName })}
          color="error"
          variant="contained"
        >
          {t('ats.detail.discardConfirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsDiscardDialog;
